package club.srams.website.pages

import androidx.compose.runtime.*
import club.srams.website.HeadlineTextStyle
import club.srams.website.components.form.ContactForm
import club.srams.website.components.layouts.PageLayoutData
import club.srams.website.components.utility.Constant.AWESOME_PLACES_EMAIL_ADDRESS
import club.srams.website.pages.AwesomePlacesColors.AMBER_DIM
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.Colors.DarkGreen
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─── Color tokens ───────────────────────────────────────────────────────────
private object AwesomePlacesColors {
    val BG_PAGE       = Color("#1C1A16")  // deep slate-brown
    val BG_SECTION    = Color("#252018")  // slightly lighter
    val BG_CARD       = Color("#2E2820")  // card surface
    val BG_CARD_HOVER = Color("#3A3028")

    val AMBER_LIGHT   = Color("#FAC775")  // headings / highlights
    val AMBER_MID     = Color("#EF9F27")  // accents
    val AMBER_DIM     = Color("#BA7517")  // borders

    val TEXT_PRIMARY  = Color("#F0EAD6")  // warm off-white
    val TEXT_MUTED    = Color("#A09880")  // secondary

    val BTN_FB        = Color("#1877F2")
    val BTN_YT        = Color("#C4302B")
    val BTN_CLUB      = Color("#2E8B57")
    val BTN_WEB       = Color("#8B6914")
}

@InitRoute
fun initAwesomePlacesPageV2(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Awesome Places"))
}

// ─── Styles ─────────────────────────────────────────────────────────────────
val PageStyle = CssStyle {
    base {
        Modifier
            .background(AwesomePlacesColors.BG_PAGE)
            .color(AwesomePlacesColors.TEXT_PRIMARY)
            .fontFamily("Georgia", "serif")
            .minHeight(100.vh)

    }
}

val HeroStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 4.cssRem, leftRight = 2.cssRem)
            .maxWidth(900.px)
            .margin(leftRight = autoLength)
            .textAlign(TextAlign.Center)
    }
}

val SectionStyle = CssStyle {
    base {
        Modifier
            .background(AwesomePlacesColors.BG_SECTION)
            .padding(topBottom = 3.cssRem, leftRight = 2.cssRem)
    }
}

val SectionInnerStyle = CssStyle {
    base {
        Modifier
            .maxWidth(900.px)
            .margin(leftRight = autoLength)
    }
}

val CardStyle = CssStyle {
    base {
        Modifier
            .background(AwesomePlacesColors.BG_CARD)
            .borderRadius(12.px)
            .border(1.px, LineStyle.Solid, AwesomePlacesColors.AMBER_DIM)
            .padding(1.5.cssRem)
            .margin(bottom = 1.cssRem)
            .transition(Transition.of("background", 0.2.s))
    }
    hover {
        Modifier.background(AwesomePlacesColors.BG_CARD_HOVER)
    }
}

val NavButtonStyle = CssStyle {
    base {
        Modifier
            .background(AwesomePlacesColors.BG_CARD)
            .border(1.px, LineStyle.Solid, AwesomePlacesColors.AMBER_MID)
            .borderRadius(24.px)
            .color(AwesomePlacesColors.AMBER_LIGHT)
            .padding(topBottom = 0.6.cssRem, leftRight = 1.4.cssRem)
            .fontSize(0.9.cssRem)
            .cursor(Cursor.Pointer)
            .transition(Transition.of("background", 0.2.s))
    }
    hover {
        Modifier.background(AwesomePlacesColors.BG_CARD_HOVER)
    }
}

val SectionHeadingStyle = CssStyle {
    base {
        Modifier
            .color(AwesomePlacesColors.AMBER_LIGHT)
            .fontSize(1.8.cssRem)
            .fontWeight(FontWeight.Normal)
            .margin(bottom = 0.4.cssRem)
            .letterSpacing(0.04.em)
    }
}

val DividerStyle = CssStyle {
    base {
        Modifier
            .width(60.px)
            .height(2.px)
            .background(AwesomePlacesColors.AMBER_MID)
            .margin(bottom = 2.cssRem)
    }
}

val ChipStyle = CssStyle {
    base {
        Modifier
            .borderRadius(4.px)
            .fontSize(0.72.cssRem)
            .fontFamily("'Courier New'", "monospace")
            .padding(topBottom = 2.px, leftRight = 8.px)
            .margin(right = 8.px)
    }
}

// ─── Data models ─────────────────────────────────────────────────────────────
data class ResourceItem(
    val title: String,
    val description: String,
    val url: String,
    val tag: String? = null
)

private val facebookPages = listOf(
    ResourceItem(
        "Shelton Rock & Mineral Society",
        "This group is for members of The Shelton Rock & Mineral Society, members of the community who are interested in rocks and minerals and/or joining the club, and rockhounds, lapidary artists and collectors from around the world.",
        "https://www.facebook.com/groups/1102691563139199",
        "Group"
    ),
    ResourceItem(
        "Washington Agate and Mineral Society",
        "Formed in 1937, Washington Agate and Mineral Society (WAMS) of Olympia is the oldest rock club in Washington. The club is recognized as a non-profit, tax-exempt  organization 501(C)(3). WAMS-Olympia is  affiliated with the Washington State Mineral Council and the Northwest Federation of Mineralogical Societies. Membership in WAMS-Olympia is open to anyone with an  interest in rocks, and lapidary arts.",
        "https://www.facebook.com/p/Washington-Agate-and-Mineral-Society-100091451719746/",
        "Page"
    ),
    ResourceItem(
        "Lapidary Equipment & Supplies Marketplace",
        "A diverse range of high-quality tools and materials for your lapidary pursuits. Buy, sell, and trade with fellow enthusiasts and professionals alike.",
        "https://www.facebook.com/groups/444584341870567",
        "Group"
    ),
)

private val youtubeVideos = listOf(
    ResourceItem(
        "Rainy Day Roots",
        "PNW rockhounding legacy - exploring - discovering - preserving our legacy",
        "https://www.youtube.com/@rainydayroots",
        "Channel"
    )
    , ResourceItem(
        "How to Identify Raw Agates: The 'Wax Luster' Test (Stop Picking Up Flint)",
        "Did you know that most rockhounds walk right past valuable agates because they mistake them for ordinary flint?",
        "https://www.youtube.com/watch?v=5Q1o04cIiwY&t=16s",
        "Educational"
    ),
    ResourceItem(
        "Learn Rock Identification with Two Geologists",
        "Geologists Nat and Paul return, this time to identify Lake Superior rocks. Join us for a walk down the beach, identifying rocks and learning short geology lessons along the way.",
        "https://www.youtube.com/watch?v=lWTgUbdccJE",
        "Field Trip"
    ),
    ResourceItem(
        "Agates & Jasper | What Do You Really Know About Them?\n",
        "Let's talk about agates and jasper some.",
        "https://www.youtube.com/watch?v=iPwB_1BMVOs&t=686s",
        "Educational"
    ),
//    ResourceItem(
//        "Lapidary 101: Cutting & Polishing Stones",
//        "Step-by-step introduction to cabochon cutting, grinding wheels, and polishing compounds.",
//        "https://www.youtube.com/watch?v=example3",
//        "Craft"
//    ),
//    ResourceItem(
//        "The Geology of the Pacific Coast",
//        "Documentary-style exploration of the tectonic and sedimentary history shaping our coastline.",
//        "https://www.youtube.com/watch?v=example4",
//        "Documentary"
//    ),
)

private val localClubs = listOf(
    ResourceItem(
        "Kitsap Mineral & Gem Society",
        "A long-standing community of artists and \"rockhounds\" have been gathering together to share in the joy and the adventure that comes along with collecting gems and minerals.",
        "https://www.kmgsrockclub.com/",
        "Nearby"
    )
    , ResourceItem(
        "Clallam County Gem & Mineral Association",
        "CCGMA is a non-profit club based in Clallam County WA.  Our purpose is to teach lapidary skills and promote the rockhounding hobby.  Whether your interest is in collecting rocks, learning to cut and polish cabochons, or taking classes, we teach members how to handle equipment and prepare stones at our lapidary shop.   We also sponsor two annual Gem & Mineral shows in Port Angeles and host assorted field trips.  We are ready to share our passion and knowledge with you, so come join us today!",
        "https://www.sequimrocks.org/",
        "Local"
    )
    , ResourceItem(
        "Maplewood Rock & Gem Club"
        , "The Maplewood Rock and Gem Club comes together every 3rd Monday of the month, all year long! Join us to share your love of rocks, gems, and good company."
        , "https://maplewoodrockclub.com/"
        ,  "Nearby"
    )
    , ResourceItem(
        "Mount Baker Rock & Gem Club"
        , "Club meetings are held the third Monday of each month ( possibly except August and December), at 7:00 pm, in the Pioneer Pavilion Community Center, 2007 Cherry St, Ferndale, WA."
        , "https://mtbakerrockclub.org/"
        ,  "Nearby"
    )
    , ResourceItem(
        "Port Townsend Rock Club",
        "The Port Townsend Rock Club is a place where individuals and families come together to share their interest in rocks and rockhounding and to develop, refine and practice their skills cutting rocks, polishing stones, making custom jewelry and creating works of art using stone and metal. Whether you are a beginner, experienced, young or old come join the club for classes, workshops, fun events,  learning from each other and more.",
        "https://www.porttownsendrockclub.org/",
        "Local"
    )
    , ResourceItem(
        "Washington Agate and Mineral Society",
        "Formed in 1937, Washington Agate and Mineral Society (WAMS) of Olympia is the oldest rock club in Washington. The club is recognized as a non-profit, tax-exempt  organization 501(C)(3). WAMS-Olympia is  affiliated with the Washington State Mineral Council and the Northwest Federation of Mineralogical Societies. Membership in WAMS-Olympia is open to anyone with an  interest in rocks, and lapidary arts",
        "https://wamsolympia.com/",
        "Local"
    )
)

private val rockWebsites = listOf(
    ResourceItem(
        "Rock Hounding"
        , "Find Rocks, Gems & Minerals Near You"
        , "https://rockhounding.org/"
        , "Information"
    )
    , ResourceItem(
        "Rock Seeker",
        "The mission of RockSeeker.com is to cultivate curiosity and connection to the natural world by empowering rock and mineral enthusiasts of all ages to discover the wonder, beauty, history, art, and science within every stone.",
        "https://rockseeker.com/",
        "Information"
    )
    , ResourceItem(
        "Washington State Department of Natural Resources",
        "Rockhounding in Washington State",
        "https://dnr.wa.gov/washington-geological-survey/explore-popular-geology/rockhounding",
        "Government"
    )
    , ResourceItem(
        "RockTumbler.com",
        "Everything you need for rock tumbling.",
        "https://rocktumbler.com/tips/stream-rounded-rough/",
        "Information"
    )
    , ResourceItem(
        "911 Metallurgist",
        "Rocks and Mineral Identification Table.",
        "https://www.911metallurgist.com/blog/mineral-identification-table/",
        "Information"
    )
)

// ─── Sub-components ──────────────────────────────────────────────────────────
@Composable
private fun ResourceCard(item: ResourceItem, chipColor: CSSColorValue) {
    Div(attrs = CardStyle.toAttrs()) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            item.tag?.let { tag ->
                Span(
                    attrs = Modifier
                        .styleModifier {
                            backgroundColor(chipColor)
                            color(AwesomePlacesColors.TEXT_PRIMARY)
                            borderRadius(4.px)
                            fontSize(0.72.cssRem)
                            fontFamily("'Courier New'", "monospace")
                            padding(2.px, 8.px)
                            property("margin-right", "10px")
                            property("flex-shrink", "0")
                        }
                        .toAttrs()
                ) { Text(tag) }
            }
            Link(
                path = item.url,
                modifier = Modifier
                    .color(AwesomePlacesColors.AMBER_LIGHT)
                    .fontSize(1.05.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .textDecorationLine(TextDecorationLine.None)
            ) { Text(item.title) }
        }
        P(
            attrs = Modifier
                .styleModifier {
                    color(AwesomePlacesColors.TEXT_MUTED)
                    fontSize(0.9.cssRem)
                    lineHeight(1.6.number)
                    margin(0.5.cssRem, 0.px, 0.px)
                }
                .toAttrs()
        ) { Text(item.description) }
    }
}

@Composable
private fun SectionHeader(title: String) {
    H2(attrs = SectionHeadingStyle.toAttrs()) { Text(title) }
    Div(attrs = DividerStyle.toAttrs()) {}
}

// ─── Main page ───────────────────────────────────────────────────────────────
@Page("awesome-places")
@Layout(".components.layouts.PageLayout")
@Composable
fun AwesomePlacesPage() {

    val backgroundColor = if (ColorMode.current == ColorMode.LIGHT) Colors.White else Colors.Black
    val isDark     = ColorMode.current == ColorMode.DARK
    var submitted by remember { mutableStateOf(false) }
    val textColor  = if (isDark) Colors.White else Colors.Black


    Box(modifier = PageStyle.toModifier()) {
        Column (
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {

            // ── Hero / Introduction ─────────────────────────────────────────
            Div(attrs = HeroStyle.toAttrs()) {
                Img(
                    src = "/images/awesome_places/rock_hammer.webp"
                    , attrs = Modifier
                        .width(64.px)
                        .height(64.px)
                        .margin(bottom = 1.cssRem)
                        .toAttrs()
                )
                H1(attrs = Modifier
                    .color(AwesomePlacesColors.AMBER_LIGHT)
                    .fontSize(2.8.cssRem)
                    .fontWeight(FontWeight.Normal)
                    .letterSpacing(0.06.em)
                    .margin(0.px, 0.px, 0.5.cssRem)
                    .toAttrs()
                ) { Text("Awesome Places to Explore") }

                P(attrs = Modifier
                    .color(AwesomePlacesColors.TEXT_MUTED)
                    .fontSize(1.05.cssRem)
                    .lineHeight(1.7)
                    .maxWidth(620.px)
                    .margin(0.px, autoLength, 2.cssRem)
                    .toAttrs()
                ) {
                    Text(
                        "A curated collection of communities, videos, clubs, and learning resources " +
                        "for rock hounds, mineral collectors, and earth science enthusiasts. " +
                        "Jump to any section below."
                    )
                }

                // Navigation buttons
                Row(
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier
                        .flexWrap(FlexWrap.Wrap)
                        .gap(0.75.cssRem)
                ) {
                    listOf(
                        "#facebook" to "📘 Facebook Pages",
                        "#youtube"  to "▶ YouTube Videos",
                        "#clubs"    to "⛏ Local Clubs",
                        "#websites" to "🌐 Rock Websites",
                    ).forEach { (anchor, label) ->
                        A(
                            href = anchor,
                            attrs = NavButtonStyle.toAttrs()
                        ) { Text(label) }
                    }
                }
            }

            // ── Facebook Pages ──────────────────────────────────────────────
            Div(attrs = SectionStyle.toAttrs { id("facebook") }) {
                Div(attrs = SectionInnerStyle.toAttrs()) {
                    SectionHeader("Facebook Pages")
                    facebookPages.forEach { ResourceCard(it, AwesomePlacesColors.BTN_FB) }
                }
            }

            // ── YouTube Videos ──────────────────────────────────────────────
            Div(attrs = Modifier.toAttrs { id("youtube") }) {
                Div(attrs = SectionInnerStyle.toAttrs()) {
                    Column(modifier = Modifier.padding(topBottom = 3.cssRem, leftRight = 2.cssRem)) {
                        SectionHeader("YouTube Videos")
                        youtubeVideos.forEach { ResourceCard(it, AwesomePlacesColors.BTN_YT) }
                    }
                }
            }

            // ── Other Clubs ─────────────────────────────────────────────────
            Div(attrs = SectionStyle.toAttrs { id("clubs") }) {
                Div(attrs = SectionInnerStyle.toAttrs()) {
                    SectionHeader("Other Clubs in the Area")
                    localClubs.forEach { ResourceCard(it, AwesomePlacesColors.BTN_CLUB) }
                }
            }

            // ── Rock Learning Websites ──────────────────────────────────────
            Div(attrs = Modifier.toAttrs { id("websites") }) {
                Div(attrs = SectionInnerStyle.toAttrs()) {
                    Column(modifier = Modifier.padding(topBottom = 3.cssRem, leftRight = 2.cssRem)) {
                        SectionHeader("Websites to Learn About Rocks and Rockhounding")
                        rockWebsites.forEach { ResourceCard(it, AwesomePlacesColors.BTN_WEB) }
                    }
                }
            }

            // ── Footer ──────────────────────────────────────────────────────
            Div(attrs = Modifier
                .textAlign(TextAlign.Center)
                .padding(2.cssRem)
                .color(AwesomePlacesColors.TEXT_MUTED)
                .fontSize(0.85.cssRem)
                .borderTop(1.px, LineStyle.Solid, AwesomePlacesColors.AMBER_DIM)
                .borderBottom(1.px, LineStyle.Solid, AwesomePlacesColors.AMBER_DIM)
                .margin(bottom = 1.cssRem)
                .toAttrs()
            ) {
                Text("Happy hunting — may every field trip yield something wonderful.")
            }
        }
    }

            // HorizontalDivider(Modifier.fillMaxWidth())

            // ── Make a suggestion ──────────────────────────────────────
    Box(modifier = Modifier
        .background(backgroundColor)
        .color(textColor)
        .fontFamily("Georgia", "serif")
        .minHeight(100.vh)
    ) {
        Column (
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Row(
                horizontalArrangement = Arrangement.Center
            ) {
                Box(modifier = Modifier
                    //.borderTop(1.px, LineStyle.Solid, AMBER_DIM)
                    .backgroundColor(backgroundColor)

                ) {
                    Column(
                        modifier = Modifier.gap(2.cssRem)
                    ) {
                        Div(HeadlineTextStyle.toAttrs()) {
                            SpanText(
                                "Suggest club, Facebook page, website or video"
                                , Modifier
                                    .fontSize(2.0.cssRem)
                                    .fontWeight(FontWeight.Normal)
                                    .backgroundColor(backgroundColor)
                                    .letterSpacing(0.06.em)
                                    .margin(0.px, 0.px, 0.5.cssRem)
                                    .color(textColor)
                                    //.textAlign(TextAlign.Center)
//                                    .color(
//                                        when (ColorMode.current) {
//                                            ColorMode.LIGHT -> Black
//                                            ColorMode.DARK -> White
//                                        }
//                                    )
                            )
                        }
                    }
                }
            }

            // ── Success banner ──────────────────────────────────────────────────────
            if (submitted) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .padding(1.cssRem)
                        .margin(bottom = 1.5.cssRem)
                        .borderRadius(8.px)
                        .backgroundColor(backgroundColor)
                        .border(1.px, LineStyle.Solid, DarkGreen)
                ) {
                    SpanText(
                        "✓ Message sent! We'll consider your suggestion.",
                        Modifier.color(DarkGreen)
                    )
                }
            }
            else {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(backgroundColor)
                        .padding(1.cssRem)
                        .margin(bottom = 1.5.cssRem)
                ) {
                    ContactForm(AWESOME_PLACES_EMAIL_ADDRESS)
                }
            }

        }

    }
}
